import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, Row, Col, Form } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#0dcaf0", color: "#000" },
    IN_PROGRESS: { backgroundColor: "#ffc107", color: "#000" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    SKIPPED: { backgroundColor: "#6c757d", color: "#fff" },
    OVERDUE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.PENDING;
};

const getTaskTypeStyle = (type) => {
  const styles = {
    CLEANING: { backgroundColor: "#0dcaf0", color: "#000" },
    INSPECTION: { backgroundColor: "#0d6efd", color: "#fff" },
    MAINTENANCE: { backgroundColor: "#ffc107", color: "#000" },
    SANITIZATION: { backgroundColor: "#198754", color: "#fff" },
    SAFETY_CHECK: { backgroundColor: "#ff5454", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[type] || styles.OTHER;
};

const ProviderTaskList = ({
  tasks,
  loading,
  error,
  onRefresh,
  onFilterChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("status") || "ALL");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");

  useEffect(() => {
    const status = searchParams.get("status") || "ALL";
    setActiveTab(status);
    setDateFrom(searchParams.get("date_from") || "");
    setDateTo(searchParams.get("date_to") || "");
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = {};
    if (tab !== "ALL") params.status = tab;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    setSearchParams(params);
    onFilterChange({
      status: tab === "ALL" ? null : tab,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    });
  };

  const handleDateFilter = () => {
    const params = {};
    if (activeTab !== "ALL") params.status = activeTab;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    setSearchParams(params);
    onFilterChange({
      status: activeTab === "ALL" ? null : activeTab,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    });
  };

  const clearFilters = () => {
    setActiveTab("ALL");
    setDateFrom("");
    setDateTo("");
    setSearchParams({});
    onFilterChange({});
  };

  const getTaskCount = (status) => {
    if (status === "ALL") return tasks.length;
    if (status === "OVERDUE") return tasks.filter((t) => t.is_overdue).length;
    return tasks.filter((t) => t.status === status).length;
  };

  const filteredTasks = tasks.filter((task) => {
    if (activeTab === "ALL") return true;
    if (activeTab === "OVERDUE") return task.is_overdue;
    return task.status === activeTab;
  });

  const tabs = [
    { key: "ALL", label: "All", icon: "fa-list" },
    { key: "PENDING", label: "Pending", icon: "fa-clock-o" },
    { key: "IN_PROGRESS", label: "In Progress", icon: "fa-spinner" },
    { key: "OVERDUE", label: "Overdue", icon: "fa-exclamation-triangle" },
    { key: "COMPLETED", label: "Completed", icon: "fa-check" },
  ];

  if (loading && tasks.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="provider-task-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="fa fa-tasks me-2"></i>
          My Tasks
        </h5>
        <div>
          <button className="btn btn-secondary btn-sm me-2" onClick={onRefresh}>
            <i className="fa fa-refresh me-1"></i> Refresh
          </button>
          <Link to="/provider-portal" className="btn btn-outline-primary btn-sm">
            <i className="fa fa-dashboard me-1"></i> Dashboard
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading tasks"}
        </div>
      )}

      {/* Date Filter */}
      <Card className="mb-3">
        <Card.Header className="card-header-grey">
          <strong><i className="fa fa-filter me-2"></i>Filter by Date</strong>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4} sm={6} className="mb-2 mb-md-0">
              <Form.Group>
                <Form.Label className="small mb-1">From Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4} sm={6} className="mb-2 mb-md-0">
              <Form.Group>
                <Form.Label className="small mb-1">To Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <button className="btn btn-primary btn-sm me-2" onClick={handleDateFilter}>
                <i className="fa fa-search me-1"></i> Apply
              </button>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Status Tabs */}
      <Card>
        <Card.Header className="card-header-grey p-0">
          <ul className="nav nav-tabs" style={{ borderBottom: "none" }}>
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    border: "none",
                    borderBottom: activeTab === tab.key ? "2px solid #0d6efd" : "2px solid transparent",
                    borderRadius: "0",
                    backgroundColor: activeTab === tab.key ? "#fff" : "transparent",
                    color: tab.key === "OVERDUE" && getTaskCount("OVERDUE") > 0 ? "#ff5454" : (activeTab === tab.key ? "#0d6efd" : "#73818f"),
                  }}
                >
                  <i className={`fa ${tab.icon} me-1`}></i>
                  {tab.label} ({getTaskCount(tab.key)})
                </button>
              </li>
            ))}
          </ul>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredTasks.length === 0 ? (
            <div className="empty-table-message">
              <i className="fa fa-inbox me-2"></i>
              No tasks found
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Target</th>
                  <th>Location</th>
                  <th>Scheduled</th>
                  <th>Status</th>
                  <th style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => {
                  const isOverdue = task.is_overdue || task.status === "OVERDUE";
                  return (
                    <tr key={task.id} className={isOverdue ? "table-danger" : ""}>
                      <td>
                        <div className="d-flex align-items-center">
                          <strong>{task.task_name}</strong>
                          {task.requires_photo && (
                            <i className="fa fa-camera ms-2 text-muted" title="Photo required"></i>
                          )}
                        </div>
                        <span className="badge badge-pill me-1" style={getTaskTypeStyle(task.task_type)}>
                          {task.task_type_display || task.task_type}
                        </span>
                      </td>
                      <td>
                        <i className={`fa ${task.target_type === "machine" ? "fa-cog" : "fa-building"} me-1 text-muted`}></i>
                        {task.target_name}
                      </td>
                      <td>
                        <i className="fa fa-map-marker me-1 text-muted"></i>
                        {task.location_name}
                      </td>
                      <td>
                        <div>{moment(task.scheduled_date).format("MMM D, YYYY")}</div>
                        {task.due_time && (
                          <small className="text-muted">
                            <i className="fa fa-clock-o me-1"></i>
                            {task.due_time.substring(0, 5)}
                          </small>
                        )}
                      </td>
                      <td>
                        <span className="badge badge-pill" style={getStatusStyle(isOverdue ? "OVERDUE" : task.status)}>
                          {isOverdue ? "Overdue" : (task.status_display || task.status)}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/provider-portal/tasks/${task.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="fa fa-eye me-1"></i>
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProviderTaskList;
